import { useEffect, useRef, useState } from 'react';
import { MapPin, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { loadGoogleMapsScript } from '@/utils/loadGoogleMapsScript';
import { coverageFor, validCoordinates, type GeoPoint } from '@/utils/assignmentCoverage';

interface Props {
  lead: GeoPoint;
  partner?: GeoPoint & { name: string; radius?: number | null };
}

export function AssignmentCoverageMap({ lead, partner }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const hasLead = validCoordinates(lead);
  const hasPartner = partner && validCoordinates(partner);
  const { latitude: leadLat, longitude: leadLng } = lead;
  const partnerLat = partner?.latitude;
  const partnerLng = partner?.longitude;
  const radius = partner?.radius;
  const partnerName = partner?.name;

  useEffect(() => {
    if (!hasLead || !container.current) return;
    let disposed = false;
    let cleanup = () => {};
    setStatus('loading');
    // The map is optional. A failed provider must not block manual assignment.
    const timeout = window.setTimeout(() => { if (!disposed) setStatus('error'); }, 15000);
    loadGoogleMapsScript().then(() => {
      if (disposed || !container.current) return;
      window.clearTimeout(timeout);
      const point = { lat: leadLat, lng: leadLng };
      const map = new google.maps.Map(container.current, {
        center: point, zoom: 11, mapTypeControl: false, streetViewControl: false,
        fullscreenControl: true, gestureHandling: 'cooperative',
      });
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(point);
      const markers: google.maps.Marker[] = [new google.maps.Marker({
        map, position: point, title: 'Τοποθεσία πελάτη', label: 'Π', zIndex: 20,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 14, fillColor: '#dc2626', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 },
      })];
      let circle: google.maps.Circle | undefined;
      let line: google.maps.Polyline | undefined;
      if (hasPartner) {
        const partnerPoint = { lat: partnerLat, lng: partnerLng };
        bounds.extend(partnerPoint);
        const coverage = coverageFor({ latitude: leadLat, longitude: leadLng }, { latitude: partnerLat, longitude: partnerLng, radius });
        const color = coverage.inRange === true ? '#047857' : coverage.status === 'near' ? '#b45309' : '#b91c1c';
        markers.push(new google.maps.Marker({ map, position: partnerPoint, title: partnerName, label: 'Σ',
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 14, fillColor: '#0284c7', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 },
        }));
        if (coverage.radiusKm !== null) {
          circle = new google.maps.Circle({ map, center: partnerPoint, radius: coverage.radiusKm * 1000,
            fillColor: color, fillOpacity: 0.08, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 2 });
          const circleBounds = circle.getBounds();
          if (circleBounds) { bounds.extend(circleBounds.getNorthEast()); bounds.extend(circleBounds.getSouthWest()); }
        }
        line = new google.maps.Polyline({ map, path: [point, partnerPoint], geodesic: true, strokeColor: color, strokeOpacity: 0.85, strokeWeight: 2 });
      }
      const fit = () => { if (hasPartner) map.fitBounds(bounds, 40); else map.setCenter(point); };
      fit();
      const resize = new ResizeObserver(fit);
      resize.observe(container.current);
      cleanup = () => { resize.disconnect(); markers.forEach(marker => marker.setMap(null)); circle?.setMap(null); line?.setMap(null); google.maps.event.clearInstanceListeners(map); };
      setStatus('ready');
    }).catch(() => { if (!disposed) { window.clearTimeout(timeout); setStatus('error'); } });
    return () => { disposed = true; window.clearTimeout(timeout); cleanup(); };
  }, [hasLead, hasPartner, leadLat, leadLng, partnerLat, partnerLng, radius, partnerName, attempt]);

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border bg-muted/20">
      <div className="border-b bg-background p-4">
        <h3 className="flex items-center gap-2 font-semibold"><MapPin className="h-4 w-4 text-sky-700" />Χάρτης κάλυψης</h3>
        <p className="mt-1 break-words text-sm text-muted-foreground">{partner ? `Προεπισκόπηση: ${partner.name}` : 'Επιλέξτε συνεργάτη για σύγκριση.'}</p>
      </div>
      <div className="relative min-h-[320px] flex-1 lg:min-h-[460px]">
        <div ref={container} className="absolute inset-0" aria-label="Χάρτης πελάτη και ακτίνας συνεργάτη" />
        {(!hasLead || status !== 'ready') && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted/90 p-6 text-center" role="status">
          <MapPin className="h-7 w-7 text-muted-foreground" />
          <p className="max-w-xs text-sm">{!hasLead ? 'Δεν υπάρχουν έγκυρες συντεταγμένες πελάτη. Η απόσταση δεν μπορεί να υπολογιστεί.' : status === 'error' ? 'Ο χάρτης δεν φόρτωσε. Τα στοιχεία απόστασης και η ανάθεση παραμένουν διαθέσιμα.' : 'Φόρτωση χάρτη κάλυψης…'}</p>
          {hasLead && status === 'error' && <Button variant="outline" onClick={() => setAttempt(value => value + 1)}><RefreshCw className="mr-2 h-4 w-4" />Δοκιμή ξανά</Button>}
        </div>}
      </div>
      <div className="space-y-2 border-t bg-background p-4 text-xs text-muted-foreground">
        <p><span className="font-semibold text-red-700">Π: Πελάτης</span> · <span className="font-semibold text-sky-700">Σ: Συνεργάτης</span> · Κύκλος: δηλωμένη ακτίνα</p>
        <p>Ευθεία απόσταση, όχι οδική διαδρομή. Η θέση βασίζεται στα αποθηκευμένα στοιχεία και μπορεί να αφορά το κέντρο του Τ.Κ.</p>
        {partner && !hasPartner && <p className="font-medium text-amber-700">Ο συνεργάτης δεν έχει έγκυρες συντεταγμένες.</p>}
      </div>
    </div>
  );
}
